import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faSearch } from '@fortawesome/free-solid-svg-icons';

import {
  ReminderInstanceCalendarType,
  ReminderInstanceSignalType,
} from '../ProgressBars';
import {
  ALL_TYPE,
  CALENDAR_TYPE,
  SIGNAL_TYPE,
} from '../../Constants/GtemplateReminder';
import {
  getDateTriggered,
  getServiceDetails,
} from '../../Lib/GatewayReminderInstance';

export default memo(GatewayInstanceMaintenanceTable);

function GatewayInstanceMaintenanceTable(props) {
  const allInstancesChecked = useMemo(
    () => props.checkedInstances?.size === props.instances?.length,
    [props.checkedInstances?.size, props.instances?.length]
  );
  return (
    <table className="d-none d-lg-table table">
      <thead>
        <tr className="text-left nine-tenth-text">
          <th className="sticky-top bg-white border-0 pl-0 align-middle text-left">
            <div className="form-check">
              <input
                checked={allInstancesChecked}
                className="form-check-input mt-n2"
                onChange={props.handleSelectAllInstances}
                type="checkbox"
              />
            </div>
          </th>
          <th className="sticky-top bg-white border-0 align-middle">
            <div className="input-group">
              <input
                className="form-control border-0 pl-0 text-muted nine-tenth-text"
                disabled={props.instancesLoading}
                name="reminder_name"
                onChange={props.handleStatePropUpdate}
                placeholder="Reminder"
                type="text"
                value={props.reminder_name}
              />
              <div className="input-group-append">
                <span className="input-group-text border-0 bg-white">
                  <FontAwesomeIcon
                    className="text-muted"
                    icon={faSearch}
                    size="xs"
                  />
                </span>
              </div>
            </div>
          </th>
          <th className="sticky-top bg-white border-0 align-middle text-center text-text-light-gray font-weight-normal text-nowrap">
            Date Triggered
          </th>
          <th className="sticky-top bg-white border-0 align-middle text-center text-text-light-gray font-weight-normal text-nowrap">
            Last Service Date
          </th>
          <th className="sticky-top bg-white border-0 align-middle text-center text-text-light-gray font-weight-normal text-nowrap">
            Last Service Done By
          </th>
          <th className="sticky-top bg-white border-0" />
          <th className="sticky-top bg-white border-0" />
        </tr>
      </thead>
      <tbody>
        {props.instances?.map?.((instance) => {
          const dateTriggered = getDateTriggered(instance),
            { contact_info, last_service_date } = getServiceDetails(instance),
            { display_precision, unit } =
              props.signalUnitMap.get(instance.GtemplateReminder?.signal_id) ||
              {},
            {
              calendar_end,
              calendar_percentage,
              calendar_progress,
              calendar_text,
              calendar_upcoming,
              signal_current,
              signal_end,
              signal_margin,
              signal_percentage,
              signal_progress,
              signal_text,
            } = props.instanceShadowValueMap.get(instance._id),
            isCompletedOneTimeReminder =
              !instance.GtemplateReminder?.recurring &&
              !!instance.MaintenanceHistoryLogs?.length,
            [{ signal_value_when_serviced } = {}] =
              instance.MaintenanceHistoryLogs ?? [];

          return (
            <tr key={instance._id}>
              <td className="align-middle pl-0">
                <div className="form-check">
                  <input
                    checked={props.checkedInstances.has(instance._id)}
                    className="form-check-input mt-n2"
                    disabled={isCompletedOneTimeReminder}
                    onChange={() =>
                      props.handleInstanceChecked([instance._id, instance])
                    }
                    type="checkbox"
                  />
                </div>
              </td>
              <td className="align-middle">
                <div className="d-flex flex-column">
                  <p className="mb-0 nine-tenth-text text-muted">
                    {instance.GtemplateReminder.name}
                  </p>
                  {(() => {
                    switch (instance.GtemplateReminder.type) {
                      case ALL_TYPE:
                        return (
                          <>
                            <div className="mt-2 mb-1">
                              <ReminderInstanceCalendarType
                                calendar_end={calendar_end}
                                calendar_percentage={calendar_percentage}
                                calendar_upcoming={calendar_upcoming}
                                instance={instance}
                                isCompletedOneTimeReminder={
                                  isCompletedOneTimeReminder
                                }
                                progress_color={calendar_progress}
                                text={calendar_text}
                              />
                            </div>
                            <div className="mt-1">
                              <ReminderInstanceSignalType
                                display_precision={display_precision}
                                instance={instance}
                                isCompletedOneTimeReminder={
                                  isCompletedOneTimeReminder
                                }
                                progress_color={signal_progress}
                                signal_current={signal_current}
                                signal_end={signal_end}
                                signal_margin={signal_margin}
                                signal_percentage={signal_percentage}
                                signal_value_when_serviced={
                                  signal_value_when_serviced
                                }
                                text={signal_text}
                                unit={unit}
                              />
                            </div>
                          </>
                        );
                      case CALENDAR_TYPE:
                        return (
                          <ReminderInstanceCalendarType
                            calendar_end={calendar_end}
                            calendar_percentage={calendar_percentage}
                            calendar_upcoming={calendar_upcoming}
                            instance={instance}
                            isCompletedOneTimeReminder={
                              isCompletedOneTimeReminder
                            }
                            progress_color={calendar_progress}
                            text={calendar_text}
                          />
                        );
                      case SIGNAL_TYPE:
                        return (
                          <ReminderInstanceSignalType
                            instance={instance}
                            isCompletedOneTimeReminder={
                              isCompletedOneTimeReminder
                            }
                            progress_color={signal_progress}
                            signal_current={signal_current}
                            signal_end={signal_end}
                            signal_margin={signal_margin}
                            signal_percentage={signal_percentage}
                            signal_value_when_serviced={
                              signal_value_when_serviced
                            }
                            text={signal_text}
                            unit={unit}
                          />
                        );
                      default:
                        return null;
                    }
                  })()}
                </div>
              </td>
              <td className="align-middle text-center text-muted text-text-light-gray three-quarter-text">
                {dateTriggered}
              </td>
              <td className="align-middle text-center text-muted text-text-light-gray three-quarter-text">
                {last_service_date}
              </td>
              <td className="align-middle text-center text-muted text-text-light-gray three-quarter-text">
                {contact_info}
              </td>
              <td className="align-middle text-center">
                <button
                  className="btn btn-sm btn-outline-secondary rounded-lg"
                  onClick={() =>
                    props.toggleHistoryModal({
                      gateway_reminder_instance_id: instance?._id,
                    })
                  }
                >
                  View History
                </button>
              </td>
              <td className="align-middle text-center">
                <a
                  className="btn btn-link p-0"
                  href={instance.GtemplateReminder?.attachment}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faFilePdf} size="lg" />
                </a>
              </td>
            </tr>
          );
        }) ?? null}
      </tbody>
    </table>
  );
}

GatewayInstanceMaintenanceTable.propTypes = {
  checkedInstances: PropTypes.object,
  handleInstanceChecked: PropTypes.func,
  handleSelectAllInstances: PropTypes.func,
  handleStatePropUpdate: PropTypes.func,
  instances: PropTypes.arrayOf(PropTypes.object),
  instanceShadowValueMap: PropTypes.object,
  instancesLoading: PropTypes.bool,
  reminder_name: PropTypes.string,
  signalUnitMap: PropTypes.object,
  toggleHistoryModal: PropTypes.func,
};
